/**
 * Session lifecycle REST client (core-architecture §4). Mirrors the S0 backend's
 * SessionEndpoints.cs: create / list (public, cursor-paginated) / detail / join (by id or code) /
 * leave. Shapes are camelCase, matching the backend's SessionDtos serialized with the camelCase
 * naming policy.
 */
import type { AckPayload } from '../protocol/index.ts';
import { uuidv7 } from '../protocol/index.ts';
import type { HttpClient } from './http.ts';

export interface SessionListItem {
  id: string;
  name: string;
  visibility: string;
  status: string;
  ownerId: string;
  playerCount: number;
  maxPlayers: number | null;
  createdAt: string;
}

/**
 * One packaged deck a session can be created from (DEC-17 M4 increment 8b).
 *
 * This exists because four of the five shipped decks were unreachable: both clients hard-coded
 * `signal-triage@1` at create, the API accepted any `scenarioRef`, and nothing let a client find out
 * what the other refs were. Authored content no live path reaches is the register's dead-content
 * pattern, and a picker with nothing to list is how it would have happened again.
 */
export interface ScenarioCard {
  /** The `id@version` a create request carries verbatim. */
  ref: string;
  id: string;
  version: number;
  title: string;
  blurb: string | null;
  /**
   * `excerpt` or `sim` (DEC-17 M4 §1.1). The two are different OFFERS - joining a team mid-crisis
   * against founding a company - so a client phrases them differently. Carried rather than inferred
   * from a missing deadline, because that would be the client guessing at what the deck already states.
   */
  frame: 'excerpt' | 'sim';
  seats: number;
  /** The round budget for an excerpt; null for a sim, which is open-ended by construction. */
  deadline: number | null;
}

export interface SessionPage {
  items: SessionListItem[];
  nextCursor: string | null;
}

export interface SessionMemberView {
  playerId: string;
  roleKey: string | null;
  state: string;
  joinedAt: string;
  /** What this player calls themselves; empty when they signed in anonymously. */
  displayName?: string;
}

export interface SessionDetail {
  id: string;
  name: string;
  visibility: string;
  status: string;
  ownerId: string;
  playerCount: number;
  maxPlayers: number | null;
  members: SessionMemberView[];
  createdAt: string;
  /** The private-session join code — returned by the backend ONLY to the owner (REC-2); null otherwise. */
  joinCode?: string | null;
}

/** The engine-computed retro package (backend RetroAssembler; the LLM-off reflection blocks). */
export interface RetroPackage {
  scenario: string;
  outcome: string;
  score: number | null;
  rounds: number;
  ended: boolean;
  headline: string;
  /**
   * What the survival score is MADE OF (DEC-17 M4 §3), present only at a collapse.
   *
   * One number hides four different achievements: a team that lasted ninety mediocre rounds and a team
   * that burned brilliantly for twenty can print the same total. The inputs and the weights travel
   * rather than the products, so a client can show its working and the formula still lives in exactly
   * one place.
   */
  survival?: {
    final: number;
    peak: number;
    rounds: number;
    gates: number;
    peakWeight: number;
    roundWeight: number;
    gateWeight: number;
  } | null;
  meters: Record<string, number>;
  metricArcs: Record<string, number[]>;
  moodArc: { round: number; seat: string; mood: string }[];
  okrs: {
    objective: string | null;
    keyResults: { id: string; description: string; grade: string }[];
  };
  concerns: {
    id: string;
    topic: string;
    heatPeak: number;
    deferCount: number;
    status: string;
    escalated: boolean;
  }[];
  motifs: { id: string; label: string; mentions: number; retroLine: string | null }[];
  /**
   * How each decision was actually made, and the words the table wrote at the time
   * (workspace-client.md §2b). Built by the server since lane 10a item 5 and typed here for the first
   * time: the block existed, reached the retro package and the narrator's prompt, and NO CLIENT had
   * ever rendered it - which made "you wrote 'ship it, we'll fix it later' on day 4" a teaching
   * payload nobody had been shown.
   */
  decisions?: {
    id: string;
    style: string;
    owner: string;
    chose: string;
    consulted: string[];
    overrode: string[];
    tally: string[];
    deferrals: number;
    autoResolved: boolean;
    approver: string | null;
    rationales: { round: number; seat: string | null; act: string; text: string }[];
  }[];
  /** Lines written about something that was not a queued decision - a consequence event's optional note. */
  written?: { round: number; subject: string; text: string }[];
  seats: { key: string; occupant: string }[];
}

/**
 * The one shape a retro travels in, wherever it is read (backend `RetroDocument`): broadcast when the
 * session ends, committed again on `evt.retro.published` when the prose lands, and fetched on demand by
 * someone opening it later.
 *
 * `text` is absent until the retro job has published — which is exactly what tells this client to render
 * the package now and the story when it arrives. `source` says which tier wrote it, because a player
 * deserves to know whether they are reading a model or the engine's own numbers (degrade honestly).
 */
export interface RetroDocument {
  package: RetroPackage;
  text?: string | null;
  source?: 'llm' | 'scripted' | null;
}

export interface CreateSessionInput {
  name?: string;
  visibility?: 'public' | 'private';
  maxPlayers?: number;
  /** "id@version" of the scenario to run (the S1 engine bridge); omitted → a plain chat room. */
  scenarioRef?: string;
}

/**
 * The caller's OWN private retro slice (S2.5). There is no route to anyone else's: the player id comes
 * from the token, never from a path, so "read my note" cannot become "read theirs" by editing a URL.
 */
export interface PersonalRetro {
  seat: string;
  text: string;
  source: 'llm' | 'scripted';
}

/**
 * What the planning gate offers back (S2.4 §3.3). `keyResults` are ONLY the ones that compiled against
 * the session vocabulary; `rejected` counts what the validator dropped, which is shown rather than
 * hidden - a compiler that silently discards half its answer teaches nobody anything.
 */
export interface OkrSuggestion {
  objective: string;
  keyResults: { id: string; description: string; expression: string }[];
  coaching: string;
  rejected: number;
  source: 'llm' | 'scripted';
}

/** One per-round effect of a compiled policy: a metric and what it moves each round. */
export interface PolicyEffect {
  metric: string;
  value: number;
}

/**
 * What a draft compiled to (S2.4 §4.2). Either a card the validator accepted, or the reasons it did
 * not - which are the only thing the player can act on, so they are shown rather than swallowed.
 */
export interface PolicyDraft {
  card: {
    id: string;
    name: string;
    prose: string;
    effects: PolicyEffect[];
    downsideEffects: PolicyEffect[];
    processTaxRounds: number;
  } | null;
  reasons: { rule: string; message: string }[];
  source: 'llm' | 'scripted';
}

/** One private answer an NPC gave you. Never on the session channel, never in the log. */
export interface NpcReply {
  seat: string;
  about: string;
  text: string;
  source: 'llm' | 'scripted';
  /**
   * A short slice of YOUR OWN question, verified server-side as a literal substring of what you typed
   * (workspace-client.md §2c). Null when you asked by closed key, or when nothing could be quoted.
   *
   * The venetian blind: the mechanical answer is topic-shaped, and this is what shows you the specifics
   * were heard rather than pattern-matched away. It is display-only and never enters an NPC's prompt -
   * which is what lets it be your words, including whatever you chose to write.
   */
  evidence?: string | null;
}

export class SessionClient {
  constructor(private readonly http: HttpClient) {}

  create(input: CreateSessionInput): Promise<SessionDetail> {
    return this.http.post<SessionDetail>('/api/v1/sessions', {
      body: input,
      idempotencyKey: uuidv7(),
    });
  }

  listPublic(limit = 50, cursor?: string): Promise<SessionPage> {
    const query = new URLSearchParams({ limit: String(limit) });
    if (cursor) query.set('cursor', cursor);
    return this.http.get<SessionPage>(`/api/v1/sessions?${query.toString()}`);
  }

  /**
   * The sessions this player belongs to - "where was I?" (DEC-18).
   *
   * Uncursored, because it is bounded by how many teams one person is on. It deliberately includes
   * PRIVATE rooms: the server scopes it by membership, so it can only return rooms you were let into.
   */
  listMine(limit = 50): Promise<{ items: SessionListItem[] }> {
    return this.http.get<{ items: SessionListItem[] }>(`/api/v1/sessions/mine?limit=${limit}`);
  }

  /**
   * The packaged decks a session can be created from (DEC-17 M4 increment 8b).
   *
   * Uncursored: it is bounded by how much content ships, not by anything a player accumulates. Titles
   * and blurbs arrive already in the reader's language, resolved server-side from their own setting -
   * a client should not have to know the shape of a LocalizedString to draw a menu.
   */
  listScenarios(): Promise<{ scenarios: ScenarioCard[] }> {
    return this.http.get<{ scenarios: ScenarioCard[] }>('/api/v1/scenarios');
  }

  /** The retro for a game session (the teaching moment): the frozen package, and its prose once published. */
  retro(id: string): Promise<RetroDocument> {
    return this.http.get<RetroDocument>(`/api/v1/sessions/${id}/retro`);
  }

  /**
   * Ask a teammate about something on the board - by CLOSED key (`mood`, `decision:<id>`,
   * `concern:<id>`), or in your own words.
   *
   * Free text is safe here for the same reason the closed set was: the question never reaches the NPC.
   * It is resolved to one of those keys first, and a question that lands on no key is refused with the
   * menu rather than answered by an NPC improvising about state the engine does not have.
   */
  askNpc(id: string, seat: string, about: string, text?: string): Promise<NpcReply> {
    return this.http.post<NpcReply>(`/api/v1/sessions/${id}/npc/${seat}/ask`, {
      // Either a closed key or a question in the player's words (S2.4 §5). Free text is resolved to a
      // key server-side BEFORE any NPC prompt exists, so what the NPC is asked is always groundable.
      body: text === undefined ? { about } : { text },
      idempotencyKey: uuidv7(),
    });
  }

  /**
   * Turns a free-text goal into key results the engine can grade (S2.4 §3.3). ZERO state effect: the
   * reply is a suggestion, and adopting it means typing the ordinary `/objective` and `/kr` commands -
   * which is why nothing here writes and nothing here is committed.
   *
   * Every expression that comes back has already been parsed and type-checked server-side against this
   * session's vocabulary; anything that did not compile was dropped before the reply was written.
   */
  compileOkr(id: string, goal: string): Promise<OkrSuggestion> {
    return this.http.post<OkrSuggestion>(`/api/v1/sessions/${id}/okr/compile`, {
      body: { goal },
      idempotencyKey: uuidv7(),
    });
  }

  /**
   * Compiles a working agreement written in the team's words into a policy card (S2.4 §4.2).
   *
   * Commits nothing: what comes back is a PROPOSAL, already validated server-side, or the reasons it
   * was refused. Adopting it is a separate, explicit game action carrying the card - which is what puts
   * the scalars in the log and keeps the model out of replay.
   */
  draftPolicy(id: string, text: string): Promise<PolicyDraft> {
    return this.http.post<PolicyDraft>(`/api/v1/sessions/${id}/policy/draft`, {
      body: { text },
      idempotencyKey: uuidv7(),
    });
  }

  /** The caller's own conversations in this session. There is no route that names anyone else. */
  npcReplies(id: string): Promise<{ items: NpcReply[] }> {
    return this.http.get<{ items: NpcReply[] }>(`/api/v1/sessions/${id}/npc/replies`);
  }

  /** The caller's own private slice. 404 until the session-end job has written it. */
  personalRetro(id: string): Promise<PersonalRetro> {
    return this.http.get<PersonalRetro>(`/api/v1/sessions/${id}/retro/me`);
  }

  get(id: string): Promise<SessionDetail> {
    return this.http.get<SessionDetail>(`/api/v1/sessions/${id}`);
  }

  /** Join by id (public) or by joinCode (private). Returns the write-path ack (protocol.md §6). */
  join(id: string, joinCode?: string): Promise<AckPayload> {
    return this.http.post<AckPayload>(`/api/v1/sessions/${id}/members`, {
      body: joinCode ? { joinCode } : {},
      idempotencyKey: uuidv7(),
    });
  }

  leave(id: string): Promise<AckPayload> {
    return this.http.delete<AckPayload>(`/api/v1/sessions/${id}/members/me`, {
      idempotencyKey: uuidv7(),
    });
  }
}
